import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import type { AutocompleteProvider, IndentStyle } from '../types';
import { TiptapCodeAdapter } from '../autocomplete/adapters/TiptapCodeAdapter';
import { useAutocomplete } from '../autocomplete/popup/useAutocomplete';
import { AutocompletePopup } from '../autocomplete/popup/AutocompletePopup';
import { hasFormatter, formatContent, type FormatterLanguage } from '../utils/formatters';
import { getIndentString, getUnindentChars } from '../utils/indentation';
import '../styles/raw-editor.css';

/**
 * Finds the start of the current line within a text block.
 */
function findLineStart(doc: any, pos: number, blockStart: number): number {
  const textBefore = doc.textBetween(blockStart, pos, '\0', '\0');
  const lastNewline = textBefore.lastIndexOf('\n');
  return lastNewline === -1 ? blockStart : blockStart + lastNewline + 1;
}

/**
 * Gets all line start positions within a selection range.
 */
function getLineStartsInRange(doc: any, from: number, to: number, blockStart: number): number[] {
  const lineStarts: number[] = [];
  const text = doc.textBetween(blockStart, to, '\0', '\0');

  // First line start
  const firstLineStart = findLineStart(doc, from, blockStart);
  lineStarts.push(firstLineStart);

  // Find all newlines between from and to
  let searchPos = firstLineStart - blockStart;
  while (searchPos < text.length) {
    const newlinePos = text.indexOf('\n', searchPos);
    if (newlinePos === -1 || blockStart + newlinePos >= to) break;
    const lineStart = blockStart + newlinePos + 1;
    if (lineStart < to) {
      lineStarts.push(lineStart);
    }
    searchPos = newlinePos + 1;
  }

  return lineStarts;
}

const lowlight = createLowlight(common);

// Custom document that only allows a single code block
const SingleCodeBlockDocument = Document.extend({
  content: 'codeBlock',
});

export type SupportedLanguage =
  | 'plaintext'
  | 'html'
  | 'markdown'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'css'
  | 'yaml'
  | string;

export interface RawEditorProps {
  /** Current content */
  value: string;
  /** Called when content changes */
  onChange?: (content: string) => void;
  /** Language for syntax highlighting (default: 'plaintext') */
  language?: SupportedLanguage;
  /** Placeholder text */
  placeholder?: string;
  /** Additional CSS class */
  className?: string;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Autocomplete provider for Handlebars tags */
  autocompleteProvider?: AutocompleteProvider;
  /** Show friendly descriptions instead of {{variable}} syntax (default: false) */
  showDescriptions?: boolean;
  /** When true, allows any Handlebars syntax without provider validation (default: false) */
  allowFreeformHandlebars?: boolean;
  /** Indentation style: 'tab', '2space', or '4space' (default: 'tab') */
  indentStyle?: IndentStyle;
  /** Max height of the editor (e.g. "400px", "50vh"). When set, content scrolls. */
  maxHeight?: string;
}

export const RawEditor: React.FC<RawEditorProps> = ({
  value,
  onChange,
  language = 'plaintext',
  placeholder = 'Enter content...',
  className,
  readOnly = false,
  autocompleteProvider,
  showDescriptions = false,
  allowFreeformHandlebars = false,
  indentStyle = 'tab',
  maxHeight,
}) => {
  const [initialContent, setInitialContent] = useState<string | null>(null);
  const isInternalChange = useRef(false);
  const lastExternalValue = useRef(value);
  const adapterRef = useRef<TiptapCodeAdapter | null>(null);

  // Determine if this language has a formatter
  const canFormat = hasFormatter(language);

  // Format content if formatter is available
  const formatIfNeeded = useCallback(
    async (content: string): Promise<string> => {
      if (!canFormat) return content;
      return formatContent(content, language as FormatterLanguage, { indentStyle });
    },
    [canFormat, language, indentStyle]
  );

  // Format on mount
  useEffect(() => {
    formatIfNeeded(value).then((formatted) => {
      setInitialContent(formatted);
      lastExternalValue.current = value;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the effective language for lowlight (null for plaintext)
  const effectiveLanguage = language === 'plaintext' ? 'plaintext' : language;

  const editor = useEditor(
    {
      extensions: [
        SingleCodeBlockDocument,
        Text,
        History,
        CodeBlockLowlight.configure({
          lowlight,
          defaultLanguage: effectiveLanguage,
        }),
      ],
      content: initialContent
        ? {
            type: 'doc',
            content: [
              {
                type: 'codeBlock',
                attrs: { language: effectiveLanguage },
                content: [{ type: 'text', text: initialContent }],
              },
            ],
          }
        : undefined,
      editable: !readOnly,
      editorProps: {
        handleKeyDown(view, event) {
          if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
            const { state } = view;
            const { from, to } = state.selection;
            const { $from } = state.selection;
            const blockStart = $from.start();
            const blockEnd = $from.end();

            // Check if multiple lines are selected
            const lineStarts = getLineStartsInRange(state.doc, from, to, blockStart);
            const isMultiLine = lineStarts.length > 1;

            if (event.shiftKey) {
              // Shift-Tab: unindent lines
              if (isMultiLine) {
                // Unindent all selected lines
                let tr = state.tr;
                let offset = 0;

                for (const lineStart of lineStarts) {
                  const adjustedLineStart = lineStart + offset;
                  const lineText = tr.doc.textBetween(adjustedLineStart, tr.doc.resolve(adjustedLineStart).end(), '\0', '\0');
                  const firstNewline = lineText.indexOf('\n');
                  const currentLineText = firstNewline === -1 ? lineText : lineText.substring(0, firstNewline);

                  const charsToRemove = getUnindentChars(currentLineText, indentStyle);

                  if (charsToRemove > 0) {
                    tr = tr.delete(adjustedLineStart, adjustedLineStart + charsToRemove);
                    offset -= charsToRemove;
                  }
                }

                view.dispatch(tr);
              } else {
                // Single line unindent
                const lineStart = findLineStart(state.doc, from, blockStart);
                const lineText = state.doc.textBetween(lineStart, blockEnd, '\0', '\0');
                const firstNewline = lineText.indexOf('\n');
                const currentLineText = firstNewline === -1 ? lineText : lineText.substring(0, firstNewline);

                const charsToRemove = getUnindentChars(currentLineText, indentStyle);

                if (charsToRemove > 0) {
                  const tr = state.tr.delete(lineStart, lineStart + charsToRemove);
                  view.dispatch(tr);
                }
              }
            } else {
              // Tab: indent
              const indentStr = getIndentString(indentStyle);
              if (isMultiLine) {
                // Indent all selected lines
                let tr = state.tr;
                let offset = 0;

                for (const lineStart of lineStarts) {
                  const adjustedLineStart = lineStart + offset;
                  tr = tr.insertText(indentStr, adjustedLineStart, adjustedLineStart);
                  offset += indentStr.length;
                }

                view.dispatch(tr);
              } else {
                // Single cursor/selection: insert indent string (replaces selection)
                const tr = state.tr.insertText(indentStr, from, to);
                view.dispatch(tr);
              }
            }
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        isInternalChange.current = true;
        const text = editor.state.doc.textContent;
        lastExternalValue.current = text;
        onChange?.(text);
        // Reset flag after a tick
        setTimeout(() => {
          isInternalChange.current = false;
        }, 0);
      },
    },
    [initialContent, effectiveLanguage, indentStyle]
  );

  // Create adapter when editor is ready
  const adapter = useMemo(() => {
    if (!editor || !autocompleteProvider) return null;
    // Clean up old adapter
    if (adapterRef.current) {
      adapterRef.current.destroy();
    }
    const newAdapter = new TiptapCodeAdapter(editor);
    adapterRef.current = newAdapter;
    return newAdapter;
  }, [editor, autocompleteProvider]);

  // Cleanup adapter on unmount
  useEffect(() => {
    return () => {
      if (adapterRef.current) {
        adapterRef.current.destroy();
        adapterRef.current = null;
      }
    };
  }, []);

  // Create a no-op provider if none is provided (to satisfy the hook)
  const effectiveProvider = useMemo((): AutocompleteProvider => {
    if (autocompleteProvider) return autocompleteProvider;
    return {
      onQuery: async () => ({ items: [] }),
    };
  }, [autocompleteProvider]);

  // Use autocomplete hook
  const autocomplete = useAutocomplete({
    adapter,
    provider: effectiveProvider,
    showDescriptions,
    allowFreeformHandlebars,
  });

  // Handle keyboard events in capture phase to intercept before Tiptap
  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent) => {
      if (autocomplete.isOpen) {
        const handled = autocomplete.onKeyDown(event.nativeEvent);
        if (handled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    [autocomplete]
  );

  // Handle external value changes (e.g., switching from another editor)
  useEffect(() => {
    if (!editor || isInternalChange.current) return;
    if (value === lastExternalValue.current) return;

    // Value changed externally, reformat and update
    lastExternalValue.current = value;
    formatIfNeeded(value).then((formatted) => {
      editor.commands.setContent({
        type: 'doc',
        content: [
          {
            type: 'codeBlock',
            attrs: { language: effectiveLanguage },
            content: formatted ? [{ type: 'text', text: formatted }] : [],
          },
        ],
      });
    });
  }, [value, editor, formatIfNeeded, effectiveLanguage]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  if (!initialContent && initialContent !== '') {
    return (
      <div className={`raw-editor${className ? ` ${className}` : ''}`}>
        <div className="raw-editor__loading">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className={`raw-editor raw-editor--${language}${className ? ` ${className}` : ''}`}
      data-placeholder={placeholder}
      onKeyDownCapture={handleKeyDownCapture}
      style={maxHeight ? { maxHeight, overflowY: 'auto' as const } : undefined}
    >
      <EditorContent editor={editor} />
      {autocompleteProvider && (
        <AutocompletePopup
          isOpen={autocomplete.isOpen}
          items={autocomplete.items}
          position={autocomplete.position}
          selectedIndex={autocomplete.selectedIndex}
          onSelect={autocomplete.selectItem}
          onClose={autocomplete.close}
        />
      )}
    </div>
  );
};
